import refundTotalsTemplate from './refundTotals.template.html';

/*****************************************************************************
 * Component */
/**
 * @ngdoc   component
 * @module  csm.books.com.returnItems
 * @name    csm.books.com.returnItems.component:refundTotals
 * @description
 * Controls the section of the returns that calculates and displays the read-only
 * totals. The only interaction allowed by this component is selecting checks
 * from the grid. Selected checks will affect the Check Return Total, which in
 * turn, affects the Total Due.
 *****************************************************************************/
export const refundTotalsComponent = {
  controller: Controller,
  template: refundTotalsTemplate,
  bindings: {
    amounts: '=',
    cards: '<',
    checks: '<',
    hasReceiptPayments: '<',
    onShowReceiptPayments: '&',
    selectedChecks: '=',
    selectedCards: '='
  }
};

/*****************************************************************************
 * Controller */
/**
 * @ndgoc   controller
 * @module  csm.books.com.returnItems
 * @name    csm.books.com.returnItems.controller:Controller
 * @description
 * Controls the data flow for the component
 *****************************************************************************/
/* @ngInject */ function Controller($injector, $scope) {
  var $svcs = { $ctrl: this, $scope };
  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
}

/*****************************************************************************
 * initCtrl */
/**
 * Initializes controller properties and functions.
 *****************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs) {
  Object.assign($ctrl, {
    $onChanges: $injector.invoke($onChanges, null, $svcs),
    $onInit: $injector.invoke($onInit, null, $svcs),
    buildGrid: $injector.invoke(buildGrid, null, $svcs)
  });
}

/*****************************************************************************
 * $ctrl.buildGrid */
/**
 * Builds the grid for the checks and cards.
 *****************************************************************************/
/* @ngInject */ function buildGrid($ctrl, $scope, SERVER, uiGridConstants) {
  function onRegisterApi(gridApi) {
    $ctrl.paymentsGrid.api = gridApi;

    var rowSelectionChanged = function () {
      var selectedRows = $ctrl.paymentsGrid.api.selection.getSelectedRows();
      $ctrl.selectedChecks = selectedRows.filter(
        (row) => row.subType === SERVER.BooksDBConstants.LIS_CHECK_RECEIVED
      );
      $ctrl.selectedCards = selectedRows.filter(
        (row) =>
          row.subType === SERVER.BooksDBConstants.LIS_CREDIT_CARD ||
          row.subType === SERVER.BooksDBConstants.LIS_DEBIT_CARD ||
          row.subType === SERVER.BooksDBConstants.LIS_WEBSTORE
      );
    };

    $ctrl.paymentsGrid.api.selection.on.rowSelectionChanged(
      $scope,
      rowSelectionChanged
    );
    $ctrl.paymentsGrid.api.selection.on.rowSelectionChangedBatch(
      $scope,
      rowSelectionChanged
    );
  }

  return function () {
    $ctrl.paymentsGrid = {
      enableColumnMenus: false,
      enableFiltering: false,
      enableRowSelection: true,
      enableFullRowSelection: false,
      enableRowHeaderSelection: true,
      multiSelect: true,
      enableSelectAll: false,
      saveFilter: false,
      saveSort: false,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      onRegisterApi: onRegisterApi,
      minRowsToShow: 4,
      data: [],
      columnDefs: [
        {
          field: 'typeName',
          displayName: 'Type',
          width: 80
        },
        {
          field: 'number',
          width: 300,
          cellTooltip: true
        },
        {
          field: 'memo',
          cellTooltip: true
        },
        {
          field: 'amount',
          cellFilter: 'currency',
          width: 90,
          cellClass: 'text-right'
        },
        {
          field: 'returnedAmount',
          displayName: 'Refunded',
          cellFilter: 'currency',
          width: 100,
          cellClass: 'text-right',
          visible: false
        }
      ]
    };
  };
}

/*****************************************************************************
 * $ctrl.$onChanges */
/**
 * If there are any changes to the one-way binding, update the list of checks
 * and/or cards in the grid.
 *****************************************************************************/
/* @ngInject */ function $onChanges($ctrl) {
  return function (newValues, oldValues) {
    if ($ctrl.paymentsGrid) {
      $ctrl.paymentsGrid.data = ($ctrl.checks || []).concat($ctrl.cards || []);

      /*---------------------------------------------------*/
      /* Now that we have the data, we can check if we are */
      /* using cards, and display the Refunded column.     */
      /* Refreshing the grid is needed to update the UI.   */
      /*---------------------------------------------------*/
      $ctrl.paymentsGrid.columnDefs.filter(
        (column) => column.field === 'returnedAmount'
      )[0].visible = $ctrl.cards && $ctrl.cards.length > 0;
      $ctrl.paymentsGrid.api.grid.refresh();
    }
  };
}

/*****************************************************************************
 * $ctrl.$onInit */
/**
 * When the component initializes, create the property accessor to get the
 * total check amount being returned.
 *****************************************************************************/
/* @ngInject */ function $onInit($ctrl, Tools) {
  return function () {
    $ctrl.buildGrid();

    Object.defineProperty($ctrl.amounts, 'checkReturn', {
      get: function () {
        if (!$ctrl.selectedChecks) return 0;

        return Tools.roundMoney(
          $ctrl.selectedChecks.reduce(function (total, item) {
            return total + item.amount;
          }, 0)
        );
      }
    });

    /*-------------------------------------------------------------------------------------------*/
    /* The amount of money to be returned back to the card. Ensure that we do not overpay the    */
    /* item(s) being returned or overrefund a selected card. To do this, we will take the        */
    /* lowest amount between the total refund from returned items and/or convenience fee, and    */
    /* the sum of the selected card(s) to be returned.                                           */
    /*                                                                                           */
    /* Example: Total refund amount for all items is $300 and there are two cards paying         */
    /* against this, one for $100 and one for $200.                                              */
    /*                                                                                           */
    /* If the amount of the items to be returned is only $50 and we have some selected cards,    */
    /* we only want to apply $50 against the selected card(s).                                   */
    /*                                                                                           */
    /* If the amount of the items to be returned is $150, but they want to only return that to   */
    /* the $100 card, then we want to only show that $100 of the $150 is being returned by card. */
    /*-------------------------------------------------------------------------------------------*/
    Object.defineProperty($ctrl.amounts, 'cardReturn', {
      get: function () {
        if (!$ctrl.selectedCards) return 0;

        let cardTotal = $ctrl.selectedCards.reduce(
          (total, item) => total + item.amount,
          0
        );

        return Tools.roundMoney(Math.min($ctrl.amounts.refund, cardTotal));
      }
    });
  };
}
